import { Component } from '@angular/core';
import { Router, RouterLink } from '@angular/router';
import { CommonModule } from '@angular/common';

interface Service {
  title: string;
  description: string;
  link: string;
  image: string;
}

interface CaseStudy {
  title: string;
  client: string;
  description: string;
  results: string[];
  image: string;
}

interface Testimonial {
  name: string;
  role: string;
  company: string;
  content: string;
  avatar: string;
}

interface Stat {
  value: string;
  label: string;
}

@Component({
  selector: 'app-messaging',
  imports: [CommonModule, RouterLink],
  templateUrl: './messaging.html',
  styleUrl: './messaging.css',
  standalone: true
})
export class Messaging {
  services: Service[] = [
    {
      title: 'Bulk SMS',
      description: 'Reach your audience instantly with reliable SMS messaging. High delivery rates, real-time tracking, and global coverage for all your communication needs.',
      link: '/messaging/bulk-sms',
      image: 'https://images.unsplash.com/photo-1611606063065-ee7946f0787a?w=800&h=500&fit=crop'
    },
    {
      title: 'Bulk Email',
      description: 'Send professional email campaigns at scale. Advanced analytics, template design, automated workflows, and superior inbox delivery rates.',
      link: '/messaging/bulk-email',
      image: 'https://images.unsplash.com/photo-1596526131083-e8c633c948d2?w=800&h=500&fit=crop'
    },
    {
      title: 'Bulk WhatsApp',
      description: 'Connect with customers on their preferred platform. Send rich media messages, automate conversations, and engage users with interactive WhatsApp messages.',
      link: '/messaging/bulk-whatsapp',
      image: 'https://images.unsplash.com/photo-1611162616305-c69b3fa7fbe0?w=800&h=500&fit=crop'
    },
    {
      title: 'RCS Messaging',
      description: 'Next-generation SMS with rich media capabilities. Branded messages, interactive buttons, carousels, and verified sender profiles for enhanced engagement.',
      link: '/messaging/rcs',
      image: 'https://images.unsplash.com/photo-1557821552-17105176677c?w=800&h=500&fit=crop'
    },
    {
      title: 'WhatsApp API',
      description: 'Enterprise-grade WhatsApp Business API integration. Build chatbots, automate customer service, send notifications, and create seamless conversational experiences.',
      link: '/messaging/whatsapp-api',
      image: 'https://images.unsplash.com/photo-1614680376573-df3480f0c6ff?w=800&h=500&fit=crop'
    }
  ];

  caseStudies: CaseStudy[] = [
    {
      title: 'E-commerce Flash Sale Campaign',
      client: 'RetailPro Global',
      description: 'A leading e-commerce platform needed to notify 5 million customers about a 24-hour flash sale across multiple channels simultaneously.',
      results: [
        '5M+ messages delivered in under 2 hours',
        '78% open rate across all channels',
        '23% conversion rate from notifications',
        '3.2x ROI on campaign investment'
      ],
      image: 'https://images.unsplash.com/photo-1607082348824-0a96f2a4b9da?w=800&h=500&fit=crop'
    },
    {
      title: 'Healthcare Appointment Reminders',
      client: 'MediCare Network',
      description: 'A healthcare provider needed to reduce no-shows by implementing automated appointment reminders across SMS, Email, and WhatsApp.',
      results: [
        '62% reduction in missed appointments',
        '500K+ automated reminders monthly',
        '94% patient satisfaction score',
        '$2.1M saved in lost revenue annually'
      ],
      image: 'https://images.unsplash.com/photo-1576091160399-112ba8d25d1d?w=800&h=500&fit=crop'
    },
    {
      title: 'Financial Services Security Alerts',
      client: 'SecureBank International',
      description: 'A multinational bank required a reliable messaging infrastructure for sending real-time security alerts and transaction notifications to customers.',
      results: [
        '99.9% delivery success rate',
        '10M+ messages sent monthly',
        '45% reduction in fraud incidents',
        'Sub-second message delivery time'
      ],
      image: 'https://images.unsplash.com/photo-1563013544-824ae1b704d3?w=800&h=500&fit=crop'
    }
  ];

  testimonials: Testimonial[] = [
    {
      name: 'Sarah Johnson',
      role: 'Head of Digital Marketing',
      company: 'TechCorp Solutions',
      content: 'HoneyKode\'s messaging platform has transformed how we communicate with our customers. The multi-channel approach and real-time analytics have increased our engagement rates by 300%. The support team is exceptional and always ready to help.',
      avatar: 'https://images.unsplash.com/photo-1494790108377-be9c29b29330?w=150&h=150&fit=crop'
    },
    {
      name: 'Michael Chen',
      role: 'CTO',
      company: 'StartupHub Ventures',
      content: 'The API integration was seamless and the documentation is top-notch. We were able to implement enterprise-grade messaging capabilities in days, not months. The reliability and scalability have been outstanding for our growing user base.',
      avatar: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=150&h=150&fit=crop'
    },
    {
      name: 'Emily Rodriguez',
      role: 'Customer Success Director',
      company: 'GlobalRetail Inc',
      content: 'We\'ve tried multiple messaging providers, but HoneyKode stands out with their delivery rates and customer support. The ability to manage all our communication channels from one platform has saved us countless hours and improved our campaign performance significantly.',
      avatar: 'https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=150&h=150&fit=crop'
    }
  ];

  stats: Stat[] = [
    { value: '2B+', label: 'Messages Sent' },
    { value: '99.7%', label: 'Delivery Rate' },
    { value: '5,000+', label: 'Clients Served' },
    { value: '150+', label: 'Countries Reached' }
  ];

  constructor(private router: Router) {}

  navigateToService(link: string): void {
    this.router.navigate([link]);
  }
}
